import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export type Section = "requests" | "general" | "members" | "behaviour";

/**
 * One section of a settings page: its heading, an optional action beside it, the content. On a
 * page of its own (the governed-project view) it is a top-level section between hairlines; inside
 * a settings group page (`level` 3) it sits under the group's heading.
 */
export function SettingsSection(props: {
  id: Section;
  action?: ReactNode;
  level?: 2 | 3;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const Heading = props.level === 3 ? "h3" : "h2";
  return (
    <section
      id={props.id}
      aria-labelledby={`${props.id}-heading`}
      className={cn(
        "flex scroll-mt-6 flex-col",
        props.level === 3 ? "gap-3" : "gap-5 py-8 first:pt-0",
      )}
    >
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Heading
          id={`${props.id}-heading`}
          className={cn("font-semibold text-fg", props.level === 3 ? "text-sm" : "text-base")}
        >
          {t(`settings.sections.${props.id}`)}
        </Heading>
        {props.action}
      </div>
      {props.children}
    </section>
  );
}
